import React from 'react';
import {FormModal} from '../../../../Presentation/components/common';
import {AppText, AppBodyText} from '../../../../Presentation/components/ui/Typography';
import {FormField, FormSection} from '../../../../Presentation/components/common';
import {createFieldProps} from '../../../../Presentation/utils/formUtils';
import {CreateUserRequest, UserStatus} from '../../models/user.model';
import {useAddUserForm} from '../../hooks/useAddUserForm';
import {Loader2} from 'lucide-react';

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (userData: CreateUserRequest) => Promise<void>;
  loading?: boolean;
}

const STATUS_OPTIONS = [
  {value: UserStatus.ACTIVE, label: 'نشط'},
  {value: UserStatus.INACTIVE, label: 'غير نشط'}
];

/**
 * AddUserModal Component - Clean Architecture
 *
 * Follows clean architecture principles:
 * - Single Responsibility: Only handles UI rendering
 * - Dependency Inversion: Uses custom hook for business logic
 * - Separation of Concerns: Business logic in hook, UI in component
 */
export const AddUserModal: React.FC<AddUserModalProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false
}) => {
  // Use custom hook for all business logic (form state, validation, data transformation)
  const {
    formState,
    formActions,
    rolesLoading,
    roleOptions,
    handleSubmit,
    resetForm
  } = useAddUserForm({
    open,
    onSubmit,
    onSuccess: () => onClose()
  });

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <FormModal
      open={open}
      onClose={handleClose}
      onSubmit={() => formActions.submit(handleSubmit)}
      onCancel={handleClose}
      title="إنشاء مستخدم جديد"
      width="max-w-2xl"
      submitLabel="إنشاء مستخدم"
      cancelLabel="إلغاء"
      isLoading={loading || formState.isSubmitting}
      disabled={!formState.isValid}
    >
      <FormSection title="بيانات المستخدم الأساسية">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            type="text"
            label="اسم المستخدم"
            placeholder="أدخل اسم المستخدم"
            {...createFieldProps('username', formState, formActions)}
            disabled={loading}
          />

          <FormField
            type="email"
            label="البريد الإلكتروني"
            placeholder="أدخل البريد الإلكتروني"
            {...createFieldProps('email', formState, formActions)}
            disabled={loading}
          />

          <FormField
            type="text"
            label="الاسم الكامل"
            placeholder="أدخل الاسم الكامل"
            {...createFieldProps('fullName', formState, formActions)}
            disabled={loading}
          />

          <FormField
            type="tel"
            label="رقم الهاتف"
            placeholder="أدخل رقم الهاتف"
            {...createFieldProps('phoneNumber', formState, formActions)}
            disabled={loading}
          />

          <FormField
            type="password"
            label="كلمة المرور"
            placeholder="أدخل كلمة المرور"
            {...createFieldProps('password', formState, formActions)}
            disabled={loading}
          />

          {rolesLoading ? (
            <div className="flex items-center space-x-2 p-3 border border-gray-300 rounded-md bg-gray-50">
              <Loader2 className="w-4 h-4 animate-spin text-brand-500" />
              <AppBodyText className="text-gray-600">جاري تحميل الأدوار...</AppBodyText>
            </div>
          ) : (
            <FormField
              type="select"
              label="الدور"
              options={roleOptions}
              {...createFieldProps('roleId', formState, formActions)}
              disabled={loading || rolesLoading}
            />
          )}

          <FormField
            type="select"
            label="الحالة"
            options={STATUS_OPTIONS}
            {...createFieldProps('status', formState, formActions)}
            disabled={loading}
          />
        </div>
      </FormSection>
    </FormModal>
  );
};
